import { Headline2 } from 'components/Elements';
import { FormCard } from 'components/forms/FormComponents';
import useNeveForm from 'components/forms/NeveForm';
import TextField from 'components/forms/TextField';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import { HomepageContext, PasswordPattern } from './Homepage';
import strings from 'strings/strings.json';
import { createErrorMessage, fieldErrorCodes, requiredFieldErrorMessage } from 'shared/error-messages';
import React from 'react';
import { confirmResetPassword, signIn } from 'aws-amplify/auth';
import NavBackButton from 'components/buttons/NavBackButton';
import { AuthContext } from 'contextProviders/AuthContext';
import { useNavigate } from 'react-router-dom';
import Button from 'components/buttons/Button';
import { TurnstileWidget } from './TurnstileWidget';
import { linkNeveUser } from 'lib/link-neve-user';

type ConfirmPasswordResetFormInput = {
    code: string;
    password: string;
    newPassword: string;
};

export default function ConfirmPasswordResetForm(): JSX.Element {
    const navigate = useNavigate();
    const { refreshUserData } = React.useContext(AuthContext);
    const { sharedData, setState } = React.useContext(HomepageContext);

    const [error, setError] = React.useState('');
    const [resettingPassword, setResettingPassword] = React.useState(false);

    // Turnstile State
    const [turnstileToken, setTurnstileToken] = React.useState('');
    const [turnstileError, setTurnstileError] = React.useState('');
    const [turnstileResetSignal, setTurnstileResetSignal] = React.useState(0);
    const resetTurnstile = React.useCallback(() => setTurnstileResetSignal((prev) => prev + 1), []);
    const handleTurnstileTokenChange = React.useCallback((token: string) => setTurnstileToken(token), []);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useNeveForm<ConfirmPasswordResetFormInput>({ code: '' });

    async function submitConfirmPasswordReset(data: ConfirmPasswordResetFormInput) {
        if (!turnstileToken) return;

        try {
            setResettingPassword(true);
            await confirmResetPassword({
                username: sharedData.emailAddress,
                confirmationCode: data.code,
                newPassword: data.password,
            });
            await signIn({
                username: sharedData.emailAddress,
                password: data.password,
                options: {
                    clientMetadata: {
                        turnstileToken,
                        turnstileAction: 'signin',
                    },
                },
            });
            await linkNeveUser(sharedData.emailAddress);
            await refreshUserData();
            navigate('/');
        } catch (error: any) {
            resetTurnstile(); // Reset challenge on failure
            switch (error.name) {
                case 'UserNotFoundException':
                    setError(strings.homepage.errors.confirmResetPassword.userNotFoundException);
                    break;
                case 'CodeMismatchException':
                    setError(strings.homepage.errors.confirmResetPassword.codeMismatchException);
                    break;
                case 'ExpiredCodeException':
                    setError(strings.homepage.errors.confirmResetPassword.expiredCodeException);
                    break;
                case 'TooManyFailedAttemptsException':
                    setError(strings.homepage.errors.confirmResetPassword.tooManyFailedAttemptsException);
                    break;
                case 'LimitExceededException':
                    setError(strings.homepage.errors.confirmResetPassword.limitExceededException);
                    break;
                case 'NotAuthorizedException':
                    setError(strings.homepage.errors.confirmResetPassword.notAuthorizedException);
                    break;
                case 'UserNotConfirmedException':
                    setError(strings.homepage.errors.confirmResetPassword.userNotConfirmedException); //sign in doesn't seem to throw this error
                    break;
                default:
                    setError(strings.homepage.errors.confirmResetPassword.general);
                    break;
            }
        } finally {
            setResettingPassword(false);
        }
    }

    return (
        <FormCard onSubmit={handleSubmit(submitConfirmPasswordReset)} autoComplete="off">
            <Headline2>{strings.homepage.headlines.confirmPasswordReset}</Headline2>
            <NavBackButton
                backButtonTitle={''}
                backButtonAria={strings.homepage.headlines.resetPassword}
                backButtonHref={''}
                buttonFunctionOverride={() => {
                    setState('resetPassword');
                }}
            />
            <TextField
                fieldName={strings.homepage.fields.code}
                labelText={strings.homepage.fields.code}
                inputAria={strings.homepage.fields.code}
                required
                errorMessage={errors.code?.message}
                autoComplete={'one-time-code'}
                {...register('code', {
                    required: {
                        value: true,
                        message: requiredFieldErrorMessage(strings.homepage.fields.code),
                    },
                    maxLength: {
                        value: 6,
                        message: createErrorMessage(strings.homepage.fields.code, fieldErrorCodes.maxLength),
                    },
                })}
            />

            <TextField
                fieldName={strings.homepage.fields.newPassword}
                labelText={strings.homepage.fields.newPassword}
                inputAria={strings.homepage.fields.newPassword}
                type="password"
                required
                errorMessage={errors.password?.message}
                autoComplete="off"
                {...register('password', {
                    required: {
                        value: true,
                        message: requiredFieldErrorMessage(strings.homepage.fields.password),
                    },
                    maxLength: {
                        value: 100,
                        message: createErrorMessage(strings.homepage.fields.password, fieldErrorCodes.maxLength),
                    },
                    pattern: {
                        value: PasswordPattern,
                        message: strings.homepage.fields.passwordPattern,
                    },
                })}
            />

            <TextField
                fieldName={strings.homepage.fields.repeatNewPassword}
                labelText={strings.homepage.fields.repeatNewPassword}
                inputAria={strings.homepage.fields.repeatNewPassword}
                type="password"
                required
                errorMessage={errors.newPassword?.message}
                autoComplete="off"
                {...register('newPassword', {
                    required: {
                        value: true,
                        message: requiredFieldErrorMessage(strings.homepage.fields.password),
                    },
                    maxLength: {
                        value: 100,
                        message: createErrorMessage(strings.homepage.fields.password, fieldErrorCodes.maxLength),
                    },
                    pattern: {
                        value: PasswordPattern,
                        message: strings.homepage.fields.passwordPattern,
                    },
                })}
            />

            <TurnstileWidget
                action="signin"
                onTokenChange={handleTurnstileTokenChange}
                onError={setTurnstileError}
                resetSignal={turnstileResetSignal}
            />
            {turnstileError && (
                <Notification
                    title={turnstileError}
                    notificationType={NotificationType.DANGER}
                    maxWidth={'100%'}
                />
            )}

            <Button
                title={strings.homepage.buttons.signIn}
                aria={strings.homepage.buttons.signIn}
                type="submit"
                fullwidth
                submitting={resettingPassword}
                disabled={!turnstileToken}
            />
            {error && <Notification title={error} notificationType={NotificationType.DANGER} maxWidth={'100%'} />}
        </FormCard>
    );
}
